import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import Logo from '../assets/logo.webp';

const NAV_LINKS = [
  { name: 'Home', path: '/' },
  { name: 'Services', path: '/services' },
  { name: 'Testimonials', path: '/testimonials' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
  { name: 'Book Now', path: '/booking', special: true },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const handleLogoClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-[#0A192F]/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">

          {/* Logo */}
          <Link
            to="/"
            className="flex items-center space-x-3 group flex-shrink-0"
            data-testid="navbar-logo"
            onClick={handleLogoClick}
          >
            <img
              src={Logo}
              alt="Connectify Logo"
              className="w-12 h-12 object-contain rounded-full transform group-hover:scale-110 transition-transform duration-300"
            />

            <div className="flex flex-col items-end leading-tight text-right">
              <span className="bg-gradient-to-r from-slate-900 via-sky-700 to-cyan-500 bg-clip-text text-xl font-black tracking-[0.14em] text-transparent drop-shadow-[0_4px_14px_rgba(30,144,255,0.12)] dark:from-white dark:via-cyan-100 dark:to-[#64FFDA]">
                CONNECTIFY
              </span>
              <span className="-mt-0.5 text-sm font-medium uppercase text-slate-500 tracking-[0.42em] dark:text-cyan-200/80">
                IT SERVICES
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden nv:flex items-center space-x-6 xl:space-x-8">
            {NAV_LINKS.map((link) =>
              link.special ? (
                <Link
                  key={link.path}
                  to={link.path}
                  className="bg-[#1E90FF] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#1570d6] hover:shadow-lg hover:shadow-[#1E90FF]/30 transition-all duration-300 transform hover:-translate-y-0.5 whitespace-nowrap"
                >
                  {link.name}
                </Link>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-medium transition-colors duration-300 hover:text-[#1E90FF] whitespace-nowrap ${
                    isActive(link.path)
                      ? 'text-[#1E90FF]'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="nv:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800"
              aria-label="Toggle menu"
            >
              {isOpen ? (
                <X className="w-6 h-6 text-gray-700 dark:text-gray-300" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="nv:hidden bg-white dark:bg-[#0A192F] border-t border-gray-200 dark:border-gray-800">
          <div className="px-4 py-4 space-y-3">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-2 rounded-lg font-medium transition-colors duration-300 ${
                  link.special
                    ? 'bg-[#1E90FF] text-white hover:bg-[#1570d6]'
                    : isActive(link.path)
                    ? 'bg-gray-100 dark:bg-gray-800 text-[#1E90FF]'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
